import React, { useEffect, useState } from 'react';
import { Alert, DatePicker, Descriptions, Form, Input, Modal } from 'antd';
import dayjs from 'dayjs';

import { formatMoney } from '../../../../format';
import { colomboToday } from '../../../uploads/uploadFile.js';
import { StepUpCancelled } from '../../../../stepup/useStepUp.js';
import { ACTIONS, bankDateAllowed, isStale, partPayment, signedAmount } from './statementLines.js';

/** What each dialog says above its fields: the consequence, in the server's terms. */
const BODY = Object.freeze({
    approveMatch: 'The claimed subscription is reconciled against this line, and its transaction moves on. You need a code from your authenticator app.',
    rejectMatch: 'The claim is taken off and the line is open again for another match.',
    clear: 'The bank\'s realisation date: the transaction is dealt on the first dealing date after it.',
    returnLine: 'The bank dishonoured this cheque: the match is voided, the transaction waits for funds again and LIV asks the investor for the money.',
    unreconcile: 'The system matched this credit to the wrong transaction. The match is undone: the credit is unallocated again (claim it to the right'
        + ' transaction, or refund it), the transaction waits for its money until its deadline, and LIV is told. Possible only until the transaction reaches its'
        + ' dealing day. Your comment is kept on the line.',
    refund: 'Money nobody could match, sent back by the bank to the account it came from. Record the bank\'s reference.',
    manualClear: 'A left-over line nothing in the Core will match - a bank charge, a payment the Core did not make, money that is not an'
        + ' investor\'s. It leaves the outstanding lines for the Cleared view and is never matched, claimed or refunded. Your reason is kept'
        + ' on the line.'
});

/** IP-176: Approve on a claim that leaves its order owing - the transaction does not move on yet. */
const PART_APPROVE_BODY = 'This line is approved as part of the subscription\'s money: the transaction waits for the rest, and LIV hears of it only'
    + ' once the whole amount is in. You need a code from your authenticator app.';

const FIELDS = Object.freeze({
    clearedOn: { label: 'Cleared on (the bank\'s date)', date: true },
    returnedOn: { label: 'Returned on (the bank\'s date)', date: true },
    bankReference: { label: 'Bank reference', max: 100 },
    refundReference: { label: 'Reference', max: 100 },
    reason: { label: 'Reason', max: 1000, area: true }
});

/**
 * One step-up decision on a statement line (IP-111): Approve, Reject,
 * Clear, Return, Unreconcile, Refund or a manual Clear (statementLines.js ACTIONS). The dialog asks for
 * the fields the action binds, then the step-up dialog opens above it for
 * the code (notes.js STEP_UP_Z_INDEX). A refusal - before the code or after
 * it - is said here; one that means the line moved on (a 409 or 404) also
 * reads the list again (onStale). Cancelling the code sends nothing.
 *
 * Each line and each action opens on empty fields and today's date (CON-2):
 * the one form instance outlives the dialog, and antd keeps a field's value
 * when the Form unmounts - so line A's bank date, reference and reason would
 * open on line B - hence clearOnDestroy, and a reset whenever the action or
 * the line changes (a dialog reopened before its close animation ends is
 * never unmounted).
 *
 * Approve names the match's amount against the order's expected amount, and
 * a claim that pays only part of it (IP-176, statementLines.js partPayment)
 * says what is already received and what is still owed after it.
 *
 * @param {{ action: string|null, line: object|null, api: object, withStepUp: Function,
 *           onClose: () => void, onDone: (text: string) => void, onStale: () => void }} props
 */
const LineActionModal = ({ action, line, api, withStepUp, onClose, onDone, onStale }) => {
    const [form] = Form.useForm();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const spec = action ? ACTIONS[action] : null;
    const today = colomboToday();
    const part = action === 'approveMatch' ? partPayment(line) : null;

    useEffect(() => {
        if (action) {
            setError(null);
            form.resetFields();
        }
    }, [action, line?.lineId, form]);

    const submit = async (values) => {
        const fields = { ...values };
        for (const name of ['clearedOn', 'returnedOn']) if (fields[name]) fields[name] = fields[name].format('YYYY-MM-DD');
        const params = spec.params(line, fields);
        setBusy(true);
        setError(null);
        try {
            await withStepUp(spec.type, params, (stepUp) => spec.send(api, line, params, stepUp));
            onDone(spec.done(line));
        } catch (e) {
            if (e instanceof StepUpCancelled) return;
            setError(e.message);
            if (isStale(e)) onStale();
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open={Boolean(spec && line)}
            title={spec?.title}
            okText={spec?.label}
            okButtonProps={{ danger: spec?.danger, loading: busy }}
            cancelButtonProps={{ disabled: busy }}
            onOk={() => form.submit()}
            onCancel={onClose}
            rootClassName="lfoc-styles-wrapper"
            destroyOnHidden
        >
            {line && (
                <Descriptions size="small" column={1} className="statement-action-facts">
                    <Descriptions.Item label="Reference">{line.referenceId ?? '—'}</Descriptions.Item>
                    <Descriptions.Item label="Amount (LKR)">{formatMoney(signedAmount(line))}</Descriptions.Item>
                    <Descriptions.Item label="Value date">{line.valueDate}</Descriptions.Item>
                    {line.transactionRef && <Descriptions.Item label="Transaction ID">{line.transactionRef}</Descriptions.Item>}
                    {/* IP-202: the matched transaction's strategy activation code. */}
                    {line.transactionRef && <Descriptions.Item label="Strategy code">{line.strategyActivationCode ?? '—'}</Descriptions.Item>}
                    {action === 'approveMatch' && (
                        <Descriptions.Item label="Match amount (LKR)">
                            {formatMoney(line.openMatchAmount)}
                            {line.openMatchExpectedAmount ? ' of LKR ' + formatMoney(line.openMatchExpectedAmount) : ''}
                        </Descriptions.Item>
                    )}
                    {part && (
                        <Descriptions.Item label="Part payment">
                            {'LKR ' + formatMoney(part.receivedAmount) + ' already received; LKR ' + formatMoney(part.owedAfter) + ' still owed after this'}
                        </Descriptions.Item>
                    )}
                    {line.proposedByName && <Descriptions.Item label="Claimed by">{line.proposedByName}</Descriptions.Item>}
                </Descriptions>
            )}
            {action && <p>{part ? PART_APPROVE_BODY : BODY[action]}</p>}
            {error && <Alert type="error" showIcon title={error} className="auth-alert" />}
            <Form form={form} layout="vertical" onFinish={submit} requiredMark={false} clearOnDestroy
                initialValues={{ clearedOn: dayjs(today), returnedOn: dayjs(today) }}>
                {(spec?.fields ?? []).map((name) => {
                    const field = FIELDS[name];
                    const label = spec.labels?.[name] ?? field.label;
                    return (
                        <Form.Item key={name} name={name} label={label}
                            rules={[{ required: true, whitespace: !field.date, message: label + ' is needed' }]}>
                            {field.date ? (
                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" allowClear={false}
                                    disabledDate={(d) => !bankDateAllowed(line, d.format('YYYY-MM-DD'), today)} />
                            ) : field.area ? (
                                <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} maxLength={field.max} showCount />
                            ) : (
                                <Input maxLength={field.max} />
                            )}
                        </Form.Item>
                    );
                })}
            </Form>
        </Modal>
    );
};

export default LineActionModal;
